import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import { 
  Package, Truck, UserCircle, Box, AlertCircle, CheckCircle2, 
  Clock, MapPin, RefreshCw, LayoutDashboard, Search, Filter, 
  ChevronRight, ExternalLink, MoreVertical, CreditCard, Trash2, CheckSquare, Square
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAutoRefresh, setIsAutoRefresh] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [cancelModal, setCancelModal] = useState({ show: false, orderId: null, reason: "", orderUserId: null });
  
  // --- DELETE & SELECT STATES ---
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const { user } = useAuth();
  const token = user?.accessToken || user?.token;

  const steps = [
    { label: "Confirmed", status: "pending", icon: Clock, color: "text-amber-500" },
    { label: "Processing", status: "processing", icon: Box, color: "text-blue-500" },
    { label: "On Way", status: "shipped", icon: Truck, color: "text-purple-500" },
    { label: "Delivered", status: "delivered", icon: CheckCircle2, color: "text-emerald-500" },
  ];

  const fetchOrders = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const res = await axios.get("https://wethrift-backend.onrender.com/api/orders", {
        headers: { token: `Bearer ${token}` },
      });
      setOrders(res.data);
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error("Database Sync Failed!");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrders();
    let interval;
    if (isAutoRefresh) {
      interval = setInterval(() => fetchOrders(false), 30000);
    }
    return () => clearInterval(interval);
  }, [isAutoRefresh, fetchOrders]);

  // --- DELETE & SELECT LOGIC ---
  const toggleSelectOrder = (id) => {
    setSelectedOrders(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length && filteredOrders.length > 0) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o._id));
    }
  };

  const deleteSelectedOrders = async () => {
    if (!window.confirm(`System Override: Purge ${selectedOrders.length} nodes from database?`)) return;
    
    setIsDeleting(true);
    const loadingToast = toast.loading("Executing data purge...");
    
    try {
      await Promise.all(
        selectedOrders.map(id => 
          axios.delete(`https://wethrift-backend.onrender.com/api/orders/${id}`, {
            headers: { token: `Bearer ${token}` }
          })
        )
      );
      
      toast.success("Nodes Eliminated", { id: loadingToast });
      setSelectedOrders([]);
      fetchOrders(false);
    } catch (err) {
      console.error(err);
      toast.error("Purge Failed: Check Server Permissions", { id: loadingToast });
    } finally {
      setIsDeleting(false);
    }
  };

  const updateStatus = async (id, newStatus, reason = "", orderUserId) => {
    const loadingToast = toast.loading(`Initiating ${newStatus.toUpperCase()} protocol...`);
    try {
      await axios.put(`https://wethrift-backend.onrender.com/api/orders/${id}`, 
        { status: newStatus, cancellationReason: reason }, 
        { headers: { token: `Bearer ${token}` } }
      );

      if (window.socket && orderUserId) {
        window.socket.emit("admin_order_update", {
          userId: orderUserId,
          orderId: id,
          newStatus: newStatus,
          message: `Your Order #${id.slice(-6).toUpperCase()} has been updated to ${newStatus.toUpperCase()}!`
        });
      }

      toast.success(`Protocol Locked: ${newStatus.toUpperCase()}`, { id: loadingToast });
      setCancelModal({ show: false, orderId: null, reason: "", orderUserId: null });
      fetchOrders(false); 
    } catch (err) {
      console.error(err);
      toast.error("System Override Blocked!", { id: loadingToast });
    }
  };

  const filteredOrders = orders.filter(order => {
    const orderIdToSearch = order.orderNumber || order._id;
    const matchesSearch = orderIdToSearch.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === "all" || order.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="p-4 md:p-10 space-y-8 max-w-[1700px] mx-auto bg-[#050505] min-h-screen font-sans text-white selection:bg-emerald-500/30">
      <Toaster position="top-right" />

      {/* --- HEADER --- */}
      <div className="sticky top-2 lg:top-4 z-50 mx-2 lg:mx-0">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 justify-between items-stretch lg:items-center bg-zinc-950/80 backdrop-blur-xl border border-white/10 p-4 lg:p-8 rounded-[1.5rem] lg:rounded-[2.5rem] shadow-2xl transition-all duration-300">
          <div className="flex items-center gap-4 lg:gap-6">
            <div className="w-12 h-12 lg:w-16 lg:h-16 bg-emerald-500/10 rounded-[1.5rem] border border-emerald-500/20 flex items-center justify-center shrink-0 shadow-[0_0_30px_rgba(16,185,129,0.1)]">
              <LayoutDashboard className="text-emerald-500 w-6 h-6 lg:w-8 lg:h-8" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl lg:text-3xl font-[1000] italic uppercase tracking-tighter leading-none">
                Command_<span className="text-emerald-500">Center</span>
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 lg:gap-4 items-center w-full lg:w-auto">
            {/* --- DELETE BUTTON --- */}
            {selectedOrders.length > 0 && (
              <button 
                onClick={deleteSelectedOrders}
                disabled={isDeleting}
                className="flex-1 lg:flex-none bg-red-600 hover:bg-red-500 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase flex items-center justify-center gap-2 transition-all animate-pulse"
              >
                <Trash2 size={14} /> Purge_{selectedOrders.length}_Nodes
              </button>
            )}

            {/* --- SELECT ALL BUTTON --- */}
            <button 
              onClick={toggleSelectAll}
              className="flex-1 lg:flex-none bg-zinc-900 border border-white/10 px-6 py-4 rounded-2xl text-[10px] font-black uppercase flex items-center justify-center gap-2 hover:bg-zinc-800 transition-all"
            >
              {selectedOrders.length === filteredOrders.length && filteredOrders.length > 0 ? (
                <CheckSquare size={14} className="text-emerald-500" />
              ) : (
                <Square size={14} />
              )}
              Select_All
            </button>

            <div className="relative flex-1 lg:w-80 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 group-focus-within:text-emerald-500 transition-colors" size={14} />
              <input 
                type="text" 
                placeholder="Search_Node..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-black/40 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-[10px] font-black uppercase tracking-widest focus:border-emerald-500/40 outline-none transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* --- STATS --- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((s, idx) => (
          <button 
            key={idx}
            onClick={() => setFilterStatus(s.status)}
            className={`p-6 rounded-[2rem] border transition-all text-left group ${filterStatus === s.status ? 'bg-white/5 border-white/20 shadow-[0_0_30px_rgba(255,255,255,0.02)]' : 'bg-zinc-950/30 border-white/5 hover:border-white/10'}`}
          >
            <div className={`w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform ${s.color}`}>
              <s.icon size={20} />
            </div>
            <p className="text-[9px] font-black text-zinc-500 uppercase tracking-widest">{s.label}</p>
            <p className="text-2xl font-black italic mt-1">{orders.filter(o => o.status === s.status).length}</p>
          </button>
        ))}
      </div>

      {/* --- CARDS --- */}
      <div className="space-y-6">
        {loading ? (
          <div className="py-40 text-center text-zinc-800 text-4xl font-[1000] italic uppercase tracking-[0.5em] animate-pulse">Syncing_Nodes...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="py-40 text-center border-2 border-dashed border-white/5 rounded-[3rem]">
            <p className="text-zinc-500 font-black uppercase tracking-widest">No matching logs found in terminal</p>
          </div>
        ) : (
          filteredOrders.map((order) => {
            const isCancelled = order.status === "cancelled";
            const isDelivered = order.status === "delivered";
            const currentOrderUserId = order.userId?._id || order.userId;
            const isSelected = selectedOrders.includes(order._id);
            
            return (
              <div key={order._id} className="flex items-start gap-4 group">
                {/* --- SELECTION CHECKBOX SIDEBAR --- */}
                <button 
                  onClick={() => toggleSelectOrder(order._id)}
                  className={`mt-10 shrink-0 w-12 h-12 rounded-2xl border transition-all flex items-center justify-center ${isSelected ? 'bg-emerald-500 border-emerald-400 text-black' : 'bg-zinc-950 border-white/5 text-zinc-800 hover:border-white/20'}`}
                >
                  {isSelected ? <CheckSquare size={24} /> : <Square size={24} />}
                </button>

                {/* --- MAIN ORDER CARD --- */}
                <div className={`flex-1 bg-[#0a0a0a] border ${isSelected ? 'border-emerald-500/40 shadow-[0_0_50px_rgba(16,185,129,0.05)]' : isCancelled ? 'border-red-900/20 grayscale-50 opacity-60' : isDelivered ? 'border-emerald-500/20' : 'border-white/5'} rounded-[3.5rem] p-6 md:p-10 transition-all hover:bg-zinc-950 shadow-2xl overflow-hidden relative`}>
                  <span className="absolute -right-10 -top-10 text-[100px] font-black text-white/[0.02] pointer-events-none uppercase italic">#{order.orderNumber?.slice(-3) || "LOG"}</span>

                  <div className="relative z-10 space-y-10">
                    <div className="flex flex-col xl:flex-row justify-between items-start gap-8">
                      <div className="flex gap-6 items-center">
                        <div className="relative">
                          <div className={`w-20 h-20 bg-zinc-900 rounded-[1.8rem] flex items-center justify-center border transition-all duration-500 ${isSelected ? 'border-emerald-500/50' : 'border-white/5'}`}>
                             <UserCircle size={40} className={`${isSelected ? 'text-emerald-500' : 'text-zinc-700'}`} />
                          </div>
                          {order.isPaid && <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-black p-1.5 rounded-lg"><CreditCard size={10} strokeWidth={3} /></div>}
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                             <span className="text-[10px] font-black text-emerald-500 bg-emerald-500/5 px-4 py-1.5 rounded-full border border-emerald-500/10 uppercase tracking-widest italic">
                               {order.orderNumber || `WTH-${order._id.slice(-6).toUpperCase()}`}
                             </span>
                             {isDelivered && <span className="text-[9px] font-black bg-emerald-500 text-black px-3 py-1 rounded-full uppercase italic">Verified</span>}
                          </div>
                          <h3 className="text-2xl font-black uppercase italic tracking-tighter truncate max-w-[200px]">{order.userId?.username || "Guest_Node"}</h3>
                          <p className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest flex items-center gap-2"><MapPin size={10} /> {order.address?.split(',')[0]}</p>
                        </div>
                      </div>

                      <div className="flex-1 w-full overflow-x-auto no-scrollbar flex gap-4 bg-white/[0.02] p-4 rounded-[2rem] border border-white/5">
                        {order.products?.map((item, idx) => (
                          <div key={idx} className="relative group/p shrink-0">
                            <img 
                              src={item.productId?.img || item.img} 
                              className="w-24 h-28 object-cover rounded-2xl border border-white/10 group-hover/p:border-emerald-500/50 transition-all duration-500" 
                              alt="thumb" 
                              crossOrigin="anonymous"
                            />
                            <div className="absolute bottom-2 right-2 bg-black/80 backdrop-blur-md px-2 py-1 rounded-lg text-[10px] font-black border border-white/10">x{item.quantity}</div>
                          </div>
                        ))}
                      </div>

                      <div className="text-right w-full xl:w-auto">
                        <p className="text-5xl font-[1000] italic tracking-tighter mb-2">₹{order.amount}</p>
                        <p className={`text-[9px] font-black uppercase tracking-widest ${order.isPaid ? 'text-emerald-500' : 'text-amber-500'}`}>
                          {order.isPaid ? '// Payment_Secure' : '// Cash_On_Delivery'}
                        </p>
                      </div>
                    </div>

                    {!isCancelled && (
                      <div className="relative py-10 px-4">
                        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-white/5 -translate-y-[25px]"></div>
                        <div className="flex justify-between items-center relative z-10 max-w-6xl mx-auto">
                          {steps.map((step, idx) => {
                            const isActive = order.status === step.status;
                            const isFinished = steps.findIndex(s => s.status === order.status) >= idx;
                            
                            return (
                              <div key={idx} className="flex flex-col items-center group/step">
                                <button 
                                  onClick={() => !isDelivered && updateStatus(order._id, step.status, "", currentOrderUserId)}
                                  disabled={isDelivered}
                                  className={`w-14 h-14 md:w-20 md:h-20 rounded-[1.8rem] flex items-center justify-center border-2 transition-all duration-700 relative ${
                                    isFinished 
                                    ? 'bg-emerald-500 border-emerald-400 text-black shadow-[0_0_30px_rgba(16,185,129,0.2)] scale-110' 
                                    : 'bg-zinc-900 border-white/5 text-zinc-600 hover:border-white/20'
                                  }`}
                                >
                                  <step.icon size={isFinished ? 28 : 24} />
                                  {isActive && <span className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full animate-ping"></span>}
                                </button>
                                <span className={`mt-5 text-[8px] md:text-[10px] font-black uppercase tracking-[0.3em] transition-colors ${isFinished ? 'text-white' : 'text-zinc-700'}`}>
                                  {step.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/5 gap-6">
                      <div className="flex items-center gap-4 text-zinc-500">
                        <Clock size={14} />
                        <p className="text-[10px] font-black uppercase tracking-widest italic">Received: {new Date(order.createdAt).toLocaleString()}</p>
                      </div>

                      <div className="flex gap-4 w-full md:w-auto">
                        {!isCancelled && !isDelivered && (
                          <button 
                            onClick={() => setCancelModal({ show: true, orderId: order._id, reason: "", orderUserId: currentOrderUserId })}
                            className="flex-1 md:flex-none px-10 py-4 rounded-2xl border border-red-500/20 text-red-500 text-[10px] font-[1000] uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all active:scale-95"
                          >
                            Kill_Node
                          </button>
                        )}
                        <button className="flex-1 md:flex-none px-10 py-4 rounded-2xl bg-zinc-900 text-white text-[10px] font-[1000] uppercase tracking-widest border border-white/5 hover:bg-white hover:text-black transition-all">
                          Full_Log
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* --- CANCEL MODAL (Existing) --- */}
      {cancelModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl">
          <div className="bg-[#0a0a0a] border border-red-500/20 w-full max-w-lg rounded-[3.5rem] p-10 shadow-[0_0_100px_rgba(239,68,68,0.1)]">
            <h2 className="text-3xl font-[1000] text-white uppercase italic text-center tracking-tighter mb-4">Confirm_<span className="text-red-600">Kill</span></h2>
            <textarea 
              className="w-full bg-zinc-900/50 border border-white/5 rounded-3xl p-6 text-white text-xs font-bold min-h-[140px] mb-8 outline-none focus:border-red-600/50 transition-all placeholder:text-zinc-700"
              placeholder="State reason for manual termination..."
              value={cancelModal.reason}
              onChange={(e) => setCancelModal({ ...cancelModal, reason: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-4">
               <button onClick={() => setCancelModal({ show: false, orderId: null, reason: "", orderUserId: null })} className="py-5 text-[10px] font-black uppercase text-zinc-500 hover:text-white transition-colors">Abort</button>
               <button 
                onClick={() => updateStatus(cancelModal.orderId, "cancelled", cancelModal.reason, cancelModal.orderUserId)}
                className="bg-red-600 hover:bg-red-500 text-white py-5 rounded-[1.5rem] font-black uppercase text-[11px] tracking-widest shadow-2xl shadow-red-600/20 transition-all"
              >
                Execute_Kill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;